import { zodResolver } from "@hookform/resolvers/zod";
import { Stack } from "@mui/material";
// eslint-disable-next-line no-restricted-imports
import { logout } from "@src/app/store/session/actions";
import { useToast } from "@src/appV2/lib";
import { EmailField } from "@src/appV2/lib/FormFields/EmailField/EmailField";
import { useLocalStorage } from "@src/appV2/lib/utils";
import { Button } from "@src/appV2/redesign/components/Button";
import { DialogFooter } from "@src/appV2/redesign/components/DialogFooter";
import { LoadingButton } from "@src/appV2/redesign/components/LoadingButton";
import { emailFormSchema } from "@src/appV2/redesign/Profile/types/emailFormSchema";
import {
  BottomSheet,
  BottomSheetIllustrationContent,
  Text,
  useModalState,
} from "@src/appV2/redesign/system/ui-components";
import { isAxiosError } from "axios";
import { useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
// eslint-disable-next-line no-restricted-imports
import { useDispatch } from "react-redux";

import { type UpdateProfileErrorResponse, useUpdateProfile } from "../../api/useUpdateProfile";
import { ProfileUpdateLocalStorageItems } from "../../constants";

interface EnterEmailStepProps {
  onEmailUpdated: () => void;
}

export function EnterEmailStep({ onEmailUpdated }: EnterEmailStepProps) {
  const { showErrorToast, showSuccessToast } = useToast();
  const dispatch = useDispatch();
  const confirmationModal = useModalState();
  const [pendingEmail, setPendingEmail] = useState<string>("");
  const [isLoggingOut, setIsLoggingOut] = useState(false);
  const [, setUpdateProfileEmailChanged] = useLocalStorage(
    ProfileUpdateLocalStorageItems.UPDATE_PROFILE_EMAIL_CHANGED,
    ""
  );

  const formMethods = useForm({
    defaultValues: {
      email: "",
    },
    resolver: zodResolver(emailFormSchema),
    mode: "onBlur",
  });

  const {
    setError,
    formState: { isValid },
  } = formMethods;

  const { mutate: updateProfile, isLoading: isUpdatingEmail } = useUpdateProfile({
    onSuccess: () => {
      // Set localStorage flag to show Stripe update prompt after login
      setUpdateProfileEmailChanged("true");

      showSuccessToast("Email updated successfully");

      // Show logging out state before the actual logout
      setIsLoggingOut(true);

      // Log out the user (same as the logout button on Accounts page)
      dispatch(logout());

      onEmailUpdated();
    },
    onError: (error) => {
      confirmationModal.closeModal();
      if (isAxiosError<UpdateProfileErrorResponse>(error) && error.response?.data) {
        const errorMessage =
          error.response.data.message ?? "Failed to update email. Please try again.";
        setError("email", {
          message: errorMessage,
        });
        showErrorToast(errorMessage);
      } else {
        const errorMessage = "Failed to update email. Please try again.";
        setError("email", {
          message: errorMessage,
        });
        showErrorToast(errorMessage);
      }
    },
  });

  const handleContinue = () => {
    const email = formMethods.watch("email");
    // Show confirmation modal instead of updating directly
    setPendingEmail(email);
    confirmationModal.openModal();
  };

  const handleConfirmUpdate = () => {
    updateProfile({
      profile: {
        email: pendingEmail,
      },
    });
  };

  return (
    <>
      <FormProvider {...formMethods}>
        <Stack sx={{ paddingY: 4, gap: 12 }}>
          <Stack spacing={6} marginBottom={12}>
            <Text variant="body2">Enter your new email address.</Text>
            <EmailField name="email" showLabel={false} />
          </Stack>
          <Stack spacing={2} sx={{ marginTop: 12 }}>
            <LoadingButton
              fullWidth
              variant="contained"
              disabled={!isValid}
              isLoading={false}
              onClick={handleContinue}
            >
              Continue
            </LoadingButton>
          </Stack>
        </Stack>
      </FormProvider>

      <BottomSheet modalState={confirmationModal}>
        <BottomSheetIllustrationContent
          illustrationType="mail-person"
          title="You need to log in again"
          description="To validate your new email, you will need to log out and log in again. This step is necessary for us to properly configure your account."
        />
        <DialogFooter orientation="vertical" sx={{ marginTop: 10 }}>
          <LoadingButton
            fullWidth
            variant="contained"
            size="large"
            isLoading={isUpdatingEmail || isLoggingOut}
            onClick={handleConfirmUpdate}
          >
            Confirm
          </LoadingButton>
          <Button
            fullWidth
            variant="outlined"
            size="large"
            onClick={() => {
              confirmationModal.closeModal();
            }}
          >
            Cancel
          </Button>
        </DialogFooter>
      </BottomSheet>
    </>
  );
}
